import { problemForm } from "@/api/client/forms";
import { DynamicForm } from "@/components/client/dynamic-form";
import { Form } from "@/components/ui/form";
import {
    Steps,
    StepsContent,
    StepsList,
    StepsTrigger,
} from "@/components/ui/stepper";
import {
    cn,
    collectFilesForUpload,
    filterActiveFormQuestions,
    generateDefaultValues,
    processDefaultValues,
    shouldRenderField, // Importamos `shouldRenderField` para usar en la validación
} from "@/lib/utils";
import { useStore } from "@/store";
import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";

const TABS = [
    { value: 1, label: "ClientLayout.forms.stepper.first" },
    { value: 2, label: "ClientLayout.forms.stepper.second" },
    { value: 3, label: "ClientLayout.forms.stepper.third" },
];

const getAdminQuestions = (t) => [
    {
        id: "date",
        alias: "date",
        type: "date",
        label: t("headColumns.communications.date"),
        required: true,
        screen: 1,
        position: -2,
        active: true,
        visibility: { form: true, summary: true },
    },
    {
        id: "source",
        alias: "source",
        type: "simpleSelect",
        label: t("ClientLayout.forms.source.label"),
        options: [
            { value: "phone", text: t("ClientLayout.forms.source.options.phone") },
            { value: "interview", text: t("ClientLayout.forms.source.options.interview") },
            { value: "email", text: t("ClientLayout.forms.source.options.email") },
            { value: "letter", text: t("ClientLayout.forms.source.options.letter") },
        ],
        required: true,
        screen: 1,
        position: -1,
        active: true,
        visibility: { form: true, summary: true },
    },
];

const withAdminDefaults = (context, values) =>
    context === "admin" ? { date: "", source: "", ...(values || {}) } : values;

const normalizeCommunicationDate = (date) =>
    typeof date === "string" && date.trim() !== "" ? `${date}T00:00:00` : undefined;

const StepItem = ({ tab, currentTab, onTabChange }) => {
    const { t } = useTranslation();

    return (
        <div className="flex items-center">
            <StepsTrigger
                value={tab.value}
                onClick={() => onTabChange(tab.value)}
                className={cn(
                    [2, 3].includes(currentTab) &&
                    tab.value <= currentTab &&
                    "bg-primary text-white border-primary",
                )}
            >
                {tab.value}
            </StepsTrigger>
            <span
                className={cn(
                    "mx-5 hidden md:flex transition-all",
                    tab.value === 3 && "!ml-5 mx-0",
                    tab.value <= currentTab && "text-primary",
                )}
            >
                {t(tab.label)}
            </span>
        </div>
    );
};

const StepDivider = ({ currentTab, index }) => (
    <div className="flex-1 flex items-center">
        <hr
            className={cn(
                "border-t border-gray-100 flex-1 mx-5 md:ml-0 transition-all",
                ((currentTab === 2 && index === 0) ||
                    (currentTab === 3 && index <= 1)) &&
                "border-primary",
            )}
        />
    </div>
);

export const ProblemForm = ({ values, context = "public" }) => {
    const { i18n } = useTranslation();
    const uploadFile = useStore((state) => state.uploadFile);
    const [questions, setQuestions] = useState([]);
    const [defaultValues, setDefaultValues] = useState(null);
    const [currentTab, setCurrentTab] = useState(1);
    const createCommunication = useStore((state) => state.createCommunication);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const formQuestions = context === "admin" ? [...getAdminQuestions(t), ...questions] : questions;

    const form = useForm({
        defaultValues: defaultValues,
    });

    const onSubmit = useCallback(
        async (values, _token) => {
            try {
                const filesToUpload = collectFilesForUpload(values, questions);
                console.log("[ProblemForm][onSubmit] Submission started", {
                    hasFiles: filesToUpload.length > 0,
                    fileCount: filesToUpload.length,
                    fileNames: filesToUpload.map(({ file }) => file.name),
                });
                const { confirmPassword, files, date, ...filteredValues } = values;
                const payloadValues = { ...filteredValues };
                questions
                    .filter((question) => question?.type === "file")
                    .forEach((question) => {
                        delete payloadValues[question.alias];
                    });
                const communicationDate = normalizeCommunicationDate(date);
                if (communicationDate) {
                    payloadValues.date = communicationDate;
                }
                const language = i18n.language;

                // Crear la comunicación primero
                console.log("[ProblemForm][onSubmit] Creating communication", { language });
                const communication = await createCommunication({
                    type: "complaint",
                    risk: "medium",
                    language: language,
                    ...payloadValues,
                });
                console.log("[ProblemForm][onSubmit] Communication created", {
                    communicationId: communication?.id,
                    accessToken: communication?.accessToken,
                });
                // Subir los archivos usando el ID de la comunicación
                if (filesToUpload.length > 0) {
                    console.log("[ProblemForm][onSubmit] Uploading files", {
                        communicationId: communication?.id,
                        fileCount: filesToUpload.length,
                        fileNames: filesToUpload.map(({ file }) => file.name),
                    });
                    const uploadPromises = filesToUpload.map(({ file, questionId }) =>
                        uploadFile(
                            file,
                            communication.id,
                            "public",
                            "communication",
                            "",
                            questionId,
                            true
                        )
                    );
                    await Promise.all(uploadPromises);
                    console.log("[ProblemForm][onSubmit] Files uploaded successfully");
                } else {
                    console.log("[ProblemForm][onSubmit] No files to upload");
                }

                form.reset(defaultValues);
                if (context === "admin") {
                    navigate(`/admin/complaints`);
                } else {
                    navigate(`/summary/${communication.accessToken}`);
                }

            } catch (error) {
                toast.error(t("ClientLayout.forms.problem.error", error));
            }
        },
        [t, form, uploadFile, createCommunication, questions, i18n.language, context, navigate, defaultValues],
    );

    // Función de validación para los campos visibles en la pestaña actual
    const validateFields = (currentTab) => {
        form.clearErrors(); // Limpiar errores previos
        const values = form.getValues(); // Obtener valores actuales del formulario
        let isValid = true;
        const newErrors = {};

        // Filtrar las preguntas de tipo password en el formulario
        const passwordQuestions = formQuestions && Array.isArray(formQuestions)
            ? formQuestions.filter((q) => q?.type === "password")
            : [];

        formQuestions
            .filter((question) => question?.screen === currentTab) // Solo preguntas de la pestaña actual
            .filter((question) => shouldRenderField(question, values, formQuestions)) // Solo preguntas visibles
            .forEach((question) => {
                const value = values[question.alias];
                newErrors[question.alias] = "";

                if (
                    question.required &&
                    (!value || (typeof value === "string" && value.trim() === ""))
                ) {
                    newErrors[question.alias] = "Este campo es obligatorio";
                    isValid = false;
                } else if (
                    question.type === "email" &&
                    value &&
                    !/^\S+@\S+\.\S+$/.test(value)
                ) {
                    newErrors[question.alias] = "Introduce un email válido";
                    isValid = false;
                } else if (question.type === "password") {
                    // Validación de longitud mínima
                    if (question.required && (!value || value.length < 6)) {
                        newErrors[question.alias] =
                            "La contraseña debe tener al menos 6 caracteres";
                        isValid = false;
                    }
                }

                if (newErrors[question.alias]) {
                    console.log(
                        `Validation failed for ${question.alias}:`,
                        newErrors[question.alias],
                    );
                }
            });

        // Establecer errores individualmente en `form`
        Object.entries(newErrors).forEach(([field, errorMessage]) => {
            if (errorMessage) {
                form.setError(field, { type: "manual", message: errorMessage });
            }
        });

        return isValid;
    };

    const onTabChange = async (newTab) => {
        if (newTab === currentTab) return;

        // Ir hacia atrás no debe bloquearse por validaciones.
        if (newTab < currentTab) {
            setCurrentTab(newTab);
            return;
        }

        const isValid = validateFields(currentTab); // Valida solo al avanzar
        if (isValid) {
            setCurrentTab(newTab);
        }
    };

    useEffect(() => {
        let active = true;
        const fetchData = async () => {
            const formData = await problemForm();
            if (!active) return;
            const questions = formData.questions;

            const filteredQuestions = filterActiveFormQuestions(questions);

            const processedValues = withAdminDefaults(context, processDefaultValues(values));
            const defaultValues =
                processedValues || withAdminDefaults(context, generateDefaultValues(filteredQuestions));
            // Initialize before exposing editable fields. Translation changes only
            // relabel the existing questions; they must never reset entered data.
            form.reset(defaultValues);
            setDefaultValues(defaultValues);
            setQuestions(filteredQuestions);
        };
        fetchData();
        return () => { active = false; };
    }, [context, form, values]);

    return (
        questions.length > 0 && (
            <div className="border border-dashed rounded-lg p-6 border-primary animate-fade-in">
                <Toaster />
                <Form {...form}>
                    <Steps className="w-full" value={currentTab}>
                        <StepsList className="flex w-full justify-between items-center mb-4">
                            {TABS.filter((tab) =>
                                formQuestions && Array.isArray(formQuestions) && formQuestions.some((q) => q?.screen === tab.value),
                            ).map((tab, index) => (
                                <React.Fragment key={index}>
                                    <StepItem
                                        tab={tab}
                                        currentTab={currentTab}
                                        onTabChange={onTabChange}
                                    />
                                    {index < 2 && (
                                        <StepDivider currentTab={currentTab} index={index} />
                                    )}
                                </React.Fragment>
                            ))}
                        </StepsList>
                        {TABS.map((tab) => (
                            <StepsContent key={tab.value} value={tab.value}>
                                <DynamicForm
                                    questions={formQuestions && Array.isArray(formQuestions) ? formQuestions.filter((q) => q?.screen === tab.value) : []}
                                    allQuestions={formQuestions}
                                    form={form}
                                    onSubmit={onSubmit}
                                    currentTab={currentTab}
                                    totalTabs={TABS.length}
                                    onPrevious={() => onTabChange(currentTab - 1)}
                                    onNext={() => onTabChange(currentTab + 1)}
                                    disabled={!!values}
                                    view={values ? "summary" : "form"}
                                    context={context}
                                />
                            </StepsContent>
                        ))}
                    </Steps>
                </Form>
            </div>
        )
    );
};
